import React, {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    basketFromCustomerBaskets,
    basketHasCheckoutLineItems,
    clearCheckoutReturn,
    hasPendingCheckoutReturn,
    isCheckoutReturnPath,
    resolveCheckoutReturnNavigation
} from '../../../utils/checkout-return-utils'

/**
 * Replays checkout return when the shopper becomes registered without using /login or SSO
 * (e.g. email verification link lands on home/account with an active session).
 * Skips /login and /sso-callback — those paths consume the cookie themselves.
 * Waits for basket. If that snapshot has no line items, refetches once before choosing
 * checkout vs cart — the first success is often empty, then the merged basket arrives.
 */
const PendingCheckoutReturn = () => {
    const {data: customer} = useCurrentCustomer()
    const {data: basket, isSuccess: isBasketSuccess, refetch} = useCurrentBasket()
    const location = useLocation()
    const navigate = useNavigation()
    const handledRef = useRef(false)

    useEffect(() => {
        if (typeof window === 'undefined') return
        if (!customer?.isRegistered) return
        if (handledRef.current) return
        if (location.pathname.includes('/sso-callback')) return
        if (location.pathname.endsWith('/login')) return
        if (!hasPendingCheckoutReturn() || !isBasketSuccess) return

        handledRef.current = true

        const redirectPendingCheckout = async () => {
            try {
                let nextBasket = basket
                if (!basketHasCheckoutLineItems(basket)) {
                    const refetchResult = await refetch()
                    nextBasket = basketFromCustomerBaskets(refetchResult?.data) ?? basket
                }

                const returnRoute = resolveCheckoutReturnNavigation(nextBasket)
                if (!returnRoute) return
                if (returnRoute === '/checkout' && isCheckoutReturnPath(location.pathname)) return
                navigate(returnRoute)
            } catch (e) {
                console.error('Error returning to checkout:', e)
                clearCheckoutReturn()
            }
        }

        redirectPendingCheckout()
    }, [customer?.isRegistered, location.pathname, navigate, isBasketSuccess, basket, refetch])

    return null
}

export default PendingCheckoutReturn
